import React from "react";
import cn from "classnames";
import s from "./PNLLineChart.module.scss";
import { IChartDataItemV2 } from "@iliotech/types";

import { useProfitHistory } from "@iliotech/data-wire";
import { AllocationOption } from "@iliotech/data-wire/src/reactHooks/context/StructureGrouping/types";
import {
  Chart,
  ChartArea,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartNavigator,
  ChartNavigatorCategoryAxis,
  ChartNavigatorSelect,
  ChartNavigatorSeries,
  ChartNavigatorSeriesItem,
  ChartNavigatorSeriesItemTooltip,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
  ChartValueAxis,
  ChartValueAxisItem,
  SharedTooltipContext,
  StockChart,
} from "@progress/kendo-react-charts";
import { PnlHistory } from "@iliotech/generated-api-v3";
import moment from "moment";
import { ClickableLegendItem, ValueLegend } from "src/main";

interface IOwnProps {
  currencyFormatter(value: number | undefined, decimal?: number): string;
  lineData?: IChartDataItemV2[];
  profitHistory: ReturnType<typeof useProfitHistory>;
  grouping: AllocationOption;
  selectedGroup?: { code: string; label: string } | undefined;
  generating?: boolean;
  height?: number;
  width?: number;
  showNavigator?: boolean;
  stackedBarMode?: boolean;
}

const BULLET_SIZE = 20;

export const PNLLineChart = ({
  currencyFormatter,
  profitHistory,
  grouping,
  selectedGroup,
  generating = false,
  height,
  width,
  showNavigator = false,
  stackedBarMode = false,
}: IOwnProps) => {
  const { lines, dates, from, to, totalLine, legendItems } =
    React.useMemo(() => {
      const lineMap: Record<string, PnlHistory & { color: string }> = {};
      const pnlData = profitHistory?.data?.data?.pnlHistory ?? [];
      const dateMap: Record<string, boolean> = {};
      const totalMap: Record<string, number> = {};

      const filteredPnlData = !!selectedGroup?.code
        ? pnlData.filter((item) => item.code === selectedGroup.code)
        : pnlData;

      // Process the data
      filteredPnlData.forEach((group) => {
        const pnlDateAllocation = stackedBarMode
          ? group.pnlDateAllocation.map((alloc, index, arr) => ({
              date: alloc.date,
              pnl: index === 0 ? alloc.pnl : alloc.pnl - arr[index - 1].pnl,
            }))
          : group.pnlDateAllocation;

        lineMap[group.code] = {
          code: group.code,
          name: group.name,
          pnlDateAllocation,
          color: grouping.getColor?.(group.code) || "black",
        };

        pnlDateAllocation.forEach((alloc) => {
          dateMap[alloc.date] = true;
          totalMap[alloc.date] = (totalMap[alloc.date] || 0) + alloc.pnl;
        });
      });

      const dates = Object.keys(dateMap).sort();
      const from = dates[0];
      const to = dates[dates.length - 1];

      // Calculate total line with daily changes if in stacked bar mode
      const totalLineData = stackedBarMode
        ? dates.map((date, index, arr) => ({
            date,
            pnl:
              index === 0
                ? totalMap[date]
                : totalMap[date] - totalMap[arr[index - 1]],
          }))
        : dates.map((date) => ({
            date,
            pnl: totalMap[date],
          }));

      const totalLine = {
        code: "total",
        name: "Total",
        pnlDateAllocation: totalLineData,
        color: "var(--illio-blue)",
      };
      const legendItems: ClickableLegendItem[] = stackedBarMode
        ? [
            {
              label: "Total",
              color: "var(--illio-blue)",
              value: currencyFormatter(
                Object.values(totalMap)[Object.values(totalMap).length - 1]
              ),
            },
            ...Object.values(lineMap).map((line) => ({
              label: line.name,
              color: line.color,
              value: currencyFormatter(
                line.pnlDateAllocation[line.pnlDateAllocation.length - 1]?.pnl
              ),
            })),
          ]
        : [
            {
              label: totalLine.name,
              color: totalLine.color,
              value: currencyFormatter(
                totalLine.pnlDateAllocation[
                  totalLine.pnlDateAllocation.length - 1
                ]?.pnl
              ),
            },
          ];

      return {
        lines: Object.values(lineMap),
        dates,
        from,
        to,
        totalLine,
        legendItems,
      };
    }, [profitHistory?.data, selectedGroup?.code, grouping, stackedBarMode]);

  const sharedTooltipRender = (context: SharedTooltipContext) => {
    const points = context.points
      ?.map((point) => ({
        label: point.series.name,
        color: point.series.color,
        value: point.value,
        series: point.series,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // Calculate total from all points when in stacked bar mode
    const total = stackedBarMode
      ? points.reduce((sum, point) => sum + (point.value || 0), 0)
      : points.find((point) => point.label === "Total")?.value || 0;

    return (
      <div className={cn(s.tooltipSurround)}>
        <div style={{ marginLeft: `calc(${BULLET_SIZE}px + 1.25rem` }}>
          {moment(context.categoryText).format("YYYY MMM DD")}
        </div>
        <ul>
          <li>
            <div className={cn(s.row)}>
              <div
                style={{
                  width: BULLET_SIZE,
                  height: BULLET_SIZE,
                  borderRadius: BULLET_SIZE / 2,
                  background: "var(--illio-blue)",
                  marginRight: 5,
                }}
              />
              <div className={cn(s.row, s.details)}>
                <div className={cn(s.label, s.total)}>
                  Total P&L:&nbsp;&nbsp;
                </div>
                <div className={cn(s.value)}>{currencyFormatter(total)}</div>
              </div>
            </div>
          </li>
          {points
            .filter((point) => point.label !== "Total")
            .map((point) => (
              <li key={point.label}>
                <div className={cn(s.row)}>
                  <div
                    style={{
                      width: BULLET_SIZE,
                      height: BULLET_SIZE,
                      borderRadius: BULLET_SIZE / 2,
                      background: point.color,
                      marginRight: 5,
                    }}
                  />
                  <div className={cn(s.row, s.details)}>
                    <div className={cn(s.label)}>
                      {point.label}:&nbsp;&nbsp;
                    </div>
                    <div className={cn(s.value)}>
                      {currencyFormatter(point.value)}
                    </div>
                  </div>
                </div>
              </li>
            ))}
        </ul>
      </div>
    );
  };

  const ChartComponent: any = showNavigator ? StockChart : Chart;

  return (
    <div>
      <ChartComponent
        transitions={false}
        style={{ height: height, width: width }}
      >
        <ChartArea background={"transparent"} height={height} width={width} />
        <ChartLegend visible={false} />
        <ChartTooltip
          opacity={0.9}
          background={"var(--dark)"}
          shared={true}
          render={sharedTooltipRender as any}
        />
        <ChartSeries>
          {stackedBarMode
            ? lines.map((series) => (
                <ChartSeriesItem
                  data={series.pnlDateAllocation}
                  key={series.code}
                  markers={{ visible: false }}
                  name={series.name}
                  field={"pnl"}
                  color={series.color}
                  categoryField={"date"}
                  type="column"
                  stack={true}
                />
              ))
            : [totalLine, ...lines].map((series) => (
                <ChartSeriesItem
                  data={series.pnlDateAllocation}
                  key={series.code}
                  markers={{ visible: false }}
                  name={series.name}
                  field={"pnl"}
                  color={series.color}
                  categoryField={"date"}
                  type={stackedBarMode ? "column" : "line"}
                  stack={stackedBarMode}
                  width={series.code === "total" ? 3 : 2}
                />
              ))}
        </ChartSeries>
        <ChartValueAxis>
          <ChartValueAxisItem
            majorGridLines={{
              color: generating ? undefined : "var(--color-lighter)",
            }}
            labels={{
              content: (e) => currencyFormatter(e.value),
              color: "var(--color)",
            }}
          />
        </ChartValueAxis>
        <ChartCategoryAxis>
          <ChartCategoryAxisItem
            type={"date"}
            baseUnit={"days"}
            maxDivisions={20}
            majorGridLines={{ visible: false }}
            minorGridLines={{ visible: false }}
            labels={{
              color: "var(--color)",
              rotation: -45,
              font: `12px "DM Sans", Nunito, sans-serif`,
              position: "start",
              content: (e) => moment(e.value).format(`DD MMM 'YY`),
            }}
            categories={dates}
          />
        </ChartCategoryAxis>
        {showNavigator && (
          <ChartNavigator>
            <ChartNavigatorSeriesItemTooltip color="white" />
            <ChartNavigatorSelect
              from={moment(from).toDate()}
              to={moment(to).toDate()}
            />
            <ChartNavigatorSeries>
              <ChartNavigatorSeriesItem
                key={totalLine.code}
                data={totalLine.pnlDateAllocation}
                type="line"
                field={"pnl"}
                categoryField={"date"}
                color={"transparent"}
                axis={"bottom"}
              />
            </ChartNavigatorSeries>
            <ChartNavigatorCategoryAxis
              majorGridLines={{
                color: generating ? undefined : "var(--color-lighter)",
              }}
              color={"var(--color-lighter)"}
              categories={dates}
              baseUnit={"years"}
              labels={{
                color: "var(--color)",
                position: "end",
              }}
            />
          </ChartNavigator>
        )}
      </ChartComponent>
    </div>
  );
};
